// free-gwp.js
// Gift with purchase: auto-adds the gift variant once the cart reaches the threshold,
// and removes it again if the cart drops below. The price itself comes from the Shopify
// automatic discount ("Free Birthday Cake Sugar Scrub 2026") – this script only adds the item.
(() => {
  const GIFT_VARIANT_ID = 41248308527271;              // Birthday Cake Sugar Scrub – Regular 16oz
  const THRESHOLD_CENTS = 12500;                       // $125
  const STARTS_AT = Date.parse('2026-09-21T08:00:00Z'); // Sep 21, 1:00 AM PDT (matches discount)
  const ENDS_AT = Date.parse('2026-09-26T08:00:00Z');   // Sep 26, 1:00 AM PDT (matches discount)
  const GIFT_PROP = '_auto_gwp';

  const isActive = () => Date.now() >= STARTS_AT && Date.now() < ENDS_AT;

  // --- helpers ---
  async function getCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
    return res.json();
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      cache: 'no-store',
      body: JSON.stringify(body)
    });
  }

  const addGift = () => postJSON('/cart/add.js', { items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { [GIFT_PROP]: 'true' } }] });
  const removeLine = (key) => postJSON('/cart/change.js', { id: key, quantity: 0 });

  // Only lines this script added are ever removed – a scrub the customer added themselves is left alone
  const isAutoGift = (i) => !!(i.properties && i.properties[GIFT_PROP] === 'true');
  const isGiftVariant = (i) => i.variant_id === GIFT_VARIANT_ID;

  // The discount only counts one-time purchases toward the threshold, so subscription lines are skipped too
  function subtotalExcludingGift(cart) {
    return cart.items.reduce((sum, i) => {
      const skip = isGiftVariant(i) || isAutoGift(i) || i.selling_plan_allocation;
      return skip ? sum : sum + i.final_line_price;
    }, 0);
  }

  // What the cart should look like: 'add', 'remove' (with line), or null when already in sync
  function pendingChange(cart) {
    const autoLine = cart.items.find(isAutoGift);
    const eligible = isActive() && subtotalExcludingGift(cart) >= THRESHOLD_CENTS;

    if (eligible && !cart.items.some(isGiftVariant)) return { type: 'add' };
    if (!eligible && autoLine) return { type: 'remove', line: autoLine };
    return null;
  }

  let lastCart = null;
  let busy = false;
  let rerun = false;

  // Returns true if the cart was modified
  async function reconcile() {
    if (busy) { rerun = true; return false; }
    busy = true;
    let changed = false;
    try {
      const cart = await getCart();
      const change = pendingChange(cart);
      if (change?.type === 'add') { await addGift(); changed = true; }
      if (change?.type === 'remove') { await removeLine(change.line.key); changed = true; }
      lastCart = changed ? await getCart() : cart;
    } catch (e) {
      console.warn('GWP reconcile error', e);
    } finally {
      busy = false;
    }

    if (changed) {
      // The cart page is server-rendered; everywhere else the theme re-renders the drawer/count on cart:refresh
      if (location.pathname.replace(/\/$/, '').endsWith('/cart')) location.reload();
      else document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
    if (rerun) { rerun = false; return (await reconcile()) || changed; }
    return changed;
  }

  // Outside the promo window there is nothing to add; just clean up any leftover auto-added gift once
  if (!isActive()) {
    if (Date.now() >= ENDS_AT) reconcile();
    return;
  }

  // --- checkout guard: make sure the gift is in the cart before leaving for checkout ---
  function isCheckoutEl(el) {
    if (!el) return false;
    if (el.matches('[name="checkout"], .cart__checkout, .checkout-button')) return true;
    if (el.closest('form[action$="/cart"]') && el.matches('[type="submit"]')) return true;
    return el.matches('a[href$="/checkout"], a[href*="/checkout?"]');
  }

  document.addEventListener('click', async (evt) => {
    const el = evt.target.closest('a, button, input[type="submit"]');
    if (!isCheckoutEl(el)) return;
    // Cart already known to be in sync: let the click go through untouched (keeps notes, attributes, etc.)
    if (lastCart && !pendingChange(lastCart)) return;

    evt.preventDefault();
    evt.stopPropagation();
    try {
      await reconcile();
    } finally {
      window.location.href = '/checkout'; // fail open
    }
  }, true);

  // --- keep the cart in sync ---
  // The theme dispatches cart:change after every add/update/remove
  let tick;
  const scheduleReconcile = () => { clearTimeout(tick); tick = setTimeout(reconcile, 250); };
  document.addEventListener('cart:change', scheduleReconcile);
  window.addEventListener('pageshow', scheduleReconcile);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcile);
  else reconcile();
})();
